import React, { useCallback, useEffect, useState } from 'react';
import { Card, Form, Button, Table, Row, Col, Alert } from 'react-bootstrap';
import api from '../../../utils/api';

// Standard reference ranges shown as hints (not medical advice, just the lab-sheet targets).
const FIELDS = [
  { key: 'totalCholesterol', label: 'Total Chol', hint: '< 200' },
  { key: 'ldl', label: 'LDL', hint: '< 100' },
  { key: 'hdl', label: 'HDL', hint: '> 40' },
  { key: 'triglycerides', label: 'Triglycerides', hint: '< 150' },
  { key: 'alt', label: 'ALT', hint: '7-56' },
  { key: 'ast', label: 'AST', hint: '10-40' },
  { key: 'alp', label: 'ALP', hint: '44-147' },
  { key: 'ggt', label: 'GGT', hint: '8-61' },
  { key: 'glucose', label: 'Glucose', hint: '70-100' },
  { key: 'a1c', label: 'A1C', hint: '< 5.7' },
];

const todayStr = () => new Date().toLocaleDateString('en-CA');
const blankForm = () => FIELDS.reduce((o, f) => ({ ...o, [f.key]: '' }), { date: todayStr(), notes: '' });

const LabsTab = () => {
  const [labs, setLabs] = useState([]);
  const [form, setForm] = useState(blankForm());
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/health/labs');
      setLabs(data);
    } catch (err) {
      setError('Failed to load lab results.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { date: form.date, notes: form.notes };
    FIELDS.forEach((f) => {
      if (form[f.key] !== '') payload[f.key] = Number(form[f.key]);
    });
    try {
      if (editingId) await api.put(`/health/labs/${editingId}`, payload);
      else await api.post('/health/labs', payload);
      setForm(blankForm());
      setEditingId(null);
      load();
    } catch (err) {
      setError('Save failed.');
    }
  };

  const edit = (lab) => {
    setEditingId(lab._id);
    const f = { date: lab.date, notes: lab.notes || '' };
    FIELDS.forEach((fd) => (f[fd.key] = lab[fd.key] ?? ''));
    setForm(f);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this lab result?')) return;
    await api.delete(`/health/labs/${id}`);
    load();
  };

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      <Card className="mb-3">
        <Card.Header>{editingId ? 'Edit lab result' : 'Add bloodwork'}</Card.Header>
        <Card.Body>
          <Form onSubmit={submit}>
            <Row className="g-2">
              <Col xs={6} md={3}>
                <Form.Label>Date</Form.Label>
                <Form.Control type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
              </Col>
              {FIELDS.map((f) => (
                <Col xs={6} md={2} key={f.key}>
                  <Form.Label>{f.label} <span className="text-muted small">({f.hint})</span></Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                </Col>
              ))}
              <Col xs={12} md={9}>
                <Form.Label>Notes</Form.Label>
                <Form.Control value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Col>
              <Col xs={12} md={3} className="d-flex align-items-end">
                <Button type="submit" className="w-100">{editingId ? 'Update' : 'Add'}</Button>
              </Col>
            </Row>
            {editingId && (
              <Button variant="link" size="sm" className="mt-2 p-0" onClick={() => { setEditingId(null); setForm(blankForm()); }}>
                Cancel edit
              </Button>
            )}
          </Form>
        </Card.Body>
      </Card>

      <Table striped hover responsive size="sm">
        <thead>
          <tr>
            <th>Date</th>
            {FIELDS.map((f) => <th key={f.key}>{f.label}</th>)}
            <th>Notes</th><th></th>
          </tr>
        </thead>
        <tbody>
          {labs.map((lab) => (
            <tr key={lab._id}>
              <td>{lab.date}</td>
              {FIELDS.map((f) => <td key={f.key}>{lab[f.key] ?? ''}</td>)}
              <td className="text-muted">{lab.notes}</td>
              <td className="text-end">
                <Button variant="link" size="sm" className="p-0 me-2" onClick={() => edit(lab)}>edit</Button>
                <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => remove(lab._id)}>delete</Button>
              </td>
            </tr>
          ))}
          {labs.length === 0 && (
            <tr><td colSpan={FIELDS.length + 3} className="text-muted">No lab results yet. Add your latest bloodwork so there's a baseline to beat.</td></tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default LabsTab;
